import { useMemo } from "react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingHeaderAttributes } from "@/hooks/buildings/useBuildingAttributes";
import type { Translations } from "@/lib/i18n/types";
import { buildHeaderFields } from "./fields";
import { FieldEditPopover } from "./FieldEditPopover";
import { FieldItem } from "./FieldItem";
import { useFieldEditor } from "./useFieldEditor";

const EHR_BASE = "https://livekluster.ehr.ee/ui/ehr/v1/building";

interface MetadataRowProps {
  /** From `public.buildings.registry_code`, not the attribute merge. */
  registryCode: string;
  attributes: BuildingHeaderAttributes | undefined;
  onFieldsSave: (fields: Record<string, string>) => Promise<void>;
  onSourceClick: (filename: string) => void;
  t: Translations;
}

/**
 * The dashboard's building-metadata row: static values whose source tooltip
 * appears on hover, each opening its own anchored edit popover on click.
 */
export function MetadataRow({
  registryCode,
  attributes,
  onFieldsSave,
  onSourceClick,
  t,
}: MetadataRowProps) {
  const { language } = useLanguage();
  const d = t.buildingDetail.dashboard;
  const tw = t.buildings.buildingDataWidget;
  const { openField, setOpenField, isSavingField, saveField } = useFieldEditor(onFieldsSave, t);
  const ehrUrl = registryCode ? `${EHR_BASE}/${registryCode}` : EHR_BASE;

  const fields = useMemo(
    () =>
      buildHeaderFields({
        registryCode,
        attributes,
        labels: { registryCode: d.registryCode, netArea: d.netArea, floors: d.floors },
      }),
    [registryCode, attributes, d]
  );

  return (
    <div className="mt-5 flex flex-wrap items-end gap-8">
      {fields.map((field) => (
        <FieldItem
          key={field.key}
          field={field}
          ehrUrl={ehrUrl}
          onSourceClick={onSourceClick}
          editLabel={d.editBuilding}
          tw={tw}
          open={openField === field.key}
          onOpenChange={(open) => setOpenField(open ? field.key : null)}
        >
          <FieldEditPopover
            field={field}
            saving={isSavingField}
            onSave={(value) => saveField(field.key, field.normalize(value))}
            onCancel={() => setOpenField(null)}
            tw={tw}
            language={language}
            saveLabel={t.common.save}
            cancelLabel={t.common.cancel}
          />
        </FieldItem>
      ))}
    </div>
  );
}
